/**
 * @file layers.ts
 * @description Central coordination point for layer management. Integrates data services, rendering engine, and UI panel.
 */

import { LayerClass, appEvents } from '@hcie/core';
import { EventBus } from '@hcie/shared';

// Modular Imports
import { 
    addLayer, 
    deleteLayer, 
    selectLayer, 
    toggleLayerVisibility, 
    moveLayer, 
    moveLayerUp, 
    moveLayerDown, 
    renameLayer,
    getActiveLayer
} from './layer-service';

import { 
    renderLayers, 
    renderTextLayer 
} from './render-engine';

import { 
    updateLayerPanelUI, 
    initLayerPanelEvents 
} from './ui/layer-panel';

// Re-exports for consumers of the layers package
export { 
    addLayer, 
    deleteLayer, 
    selectLayer, 
    toggleLayerVisibility, 
    moveLayer, 
    moveLayerUp, 
    moveLayerDown, 
    renameLayer,
    getActiveLayer,
    renderLayers,
    renderTextLayer,
    updateLayerPanelUI as updateLayerPanel
};

/**
 * @function initLayersManager
 * @description Bootstraps the layers subsystem by initializing events and initial UI state.
 * @returns {void}
 */
export function initLayersManager(): void {
    initLayerPanelEvents();
    
    // Initial UI synchronization
    setTimeout(() => {
        updateLayerPanelUI();
    }, 100);
}

/**
 * @function addVectorLayer
 * @description Shortcut to add a layer explicitly typed as 'vector'.
 * @returns {void}
 */
export function addVectorLayer(): void {
    addLayer("Vector Layer");
}

// STEP 1: Global Event Subscriptions
// Wire up EventBus and Core appEvents to the relevant service/render functions.

document.addEventListener('DOMContentLoaded', () => {
    initLayersManager();
});

// Sync with Core document switching
appEvents.addEventListener('document:switch', () => {
    renderLayers();
    updateLayerPanelUI();
});

// EventBus Listeners
EventBus.on('RENDER_LAYERS', () => renderLayers());
EventBus.on('UPDATE_LAYER_PANEL', () => updateLayerPanelUI());
EventBus.on('ADD_LAYER', (payload: { name?: string }) => addLayer(payload.name));
EventBus.on('SELECT_LAYER', (payload: { index: number }) => selectLayer(payload.index));
EventBus.on('RENDER_TEXT_LAYER', (payload: { layer: LayerClass }) => renderTextLayer(payload.layer));

/**
 * @function renderVectorLayer
 * @description Legacy bridge to render vector content (now managed within render-engine).
 * @param {LayerClass} layer - Target layer.
 */
export function renderVectorLayer(layer: LayerClass): void {
    if (layer?.type === 'vector') {
        renderLayers();
    }
}
